/* eslint-disable complexity */
import { useQuery, useInfiniteQuery } from 'react-query'
import { useRouter } from 'next/router'
import { useEffect, useReducer, useRef } from 'react'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import {
  TStockTypeList,
  TCategoryFiltersStocks,
  TCategoryStocks,
} from '../types/TCategory'
import EmptyWidget from '../Listing/EmptyWidget'
import NoFilters from '../Listing/NoFilters'

import StockListingDynamicTable from '../Listing/StocksListingDynamicTable'
import StocksListingTraitTable from '../Listing/StockListingTraitTable'
import { eventData, eventMapping } from './stock-listing.tracking'
import {
  IN_STOCKS_BG_LISTING_API_URL,
  IN_STOCKS_BG_LISTING_FILTERS,
  IN_STOCKS_LISTING_API_URL,
  IN_STOCKS_LISTING_FILTERS,
} from '@/root/api/apiConstants'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import { DOMAIN } from '@/root/constants'
import BackNavigation from '@/stories/components/Common/BackNavigation'
import { SortKeys, StockTypeList } from '@/components/Stocks/Listing/constants'
import Filter from '@/assets/common/filter.inline.svg'
import StockFilters from '@/components/Common/Filters/Filters'
import Modal from '@/stories/components/Common/Modal'
import Sorting from '@/components/Common/Filters/Sorting'
import SEOHeader from '@/components/Common/SEOHeader'
import BreadCrumb from '@/stories/components/Common/BreadCrumbs'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { useAnalyticsEvent } from '@/hooks/analytics/use-analytics-event'
import Text from '@/stories/components/Common/NewText'
import SidebarNav from '@/components/MutualFunds/Listing/SidebarNav'
import { stockCategories } from './CategoryPage/helpers'

interface IHeaders {
  Authorization: string
  Platform: string
}

interface IListing {
  headers: IHeaders
  isMobile: boolean
  listingData?: TCategoryStocks
  listingApiFailedStatusCode: number
  custHeaderPresent: boolean
  fromStrapi?: boolean
  includeBoostColumn?: boolean
  showSidebar?: boolean
}

const initialState = {
  filters: {},
  showModal: false,
  showFilterModal: false,
  pageOffset: 0,
  paginatedCal: false,
  results: [],
  updateCalCount: 0,
}

type ACTIONTYPE =
  | {
      type: 'UPDATE_FILTERS'
      filters
    }
  | {
      type: 'TOGGLE_FILTER_MODAL'
      showFilterModal: boolean
    }
  | {
      type: 'UPDATE_FILTERS_PAGINATION'
      filters
    }
  | {
      type: 'TOGGLE_MODAL'
      showModal: boolean
    }
  | {
      type: 'PAGE_COUNT'
      pageOffset: number
    }
  | {
      type: 'PAGINATED_CAL'
      paginatedCal: boolean
    }
  | {
      type: 'UPDATE_RESULTS'
      results
    }

function reducer(state: typeof initialState, action: ACTIONTYPE) {
  switch (action.type) {
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: action.filters,
        pageOffset: 0,
        updateCalCount: state.updateCalCount + 1,
      }
    case 'TOGGLE_FILTER_MODAL':
      return {
        ...state,
        showFilterModal: action.showFilterModal,
      }
    case 'TOGGLE_MODAL':
      return {
        ...state,
        showModal: action.showModal,
      }
    case 'PAGE_COUNT':
      return {
        ...state,
        pageOffset: action.pageOffset,
        paginatedCal: true,
      }
    case 'PAGINATED_CAL':
      return {
        ...state,
        paginatedCal: action.paginatedCal,
      }
    case 'UPDATE_RESULTS':
      return {
        ...state,
        results: action.results,
        paginatedCal: false,
      }
    case 'UPDATE_FILTERS_PAGINATION':
      return {
        ...state,
        filters: action.filters,
        pageOffset: 0,
      }
    default:
      return state
  }
}
/* eslint-disable max-lines-per-function */
// eslint-disable-next-line complexity
const INListing = ({
  headers,
  isMobile = false,
  listingData,
  listingApiFailedStatusCode,
  custHeaderPresent,
  fromStrapi = false,
  includeBoostColumn = false,
  showSidebar = true,
}: IListing) => {
  const router = useRouter()

  const [store, dispatch] = useReducer(reducer, initialState)
  const {
    filters = {},
    showModal = false,
    pageOffset = 0,
    updateCalCount = 0,
    showFilterModal = false,
  } = store
  const { query = {} } = router
  const { categorySlug: categoryCode = 'etf' } = query
  const stockDataTypeList = useRef<TStockTypeList[]>([])
  const typeList = stockDataTypeList.current.length
    ? stockDataTypeList.current
    : StockTypeList
  const selectedSlug = typeList.filter(
    ({ relativepath }) => relativepath === `/stocks/category/${categoryCode}`,
  )
  const {
    type: rawType = '',
    seoTitle = '',
    seoDescription,
  } = selectedSlug[0] ?? {
    type: categoryCode,
    seoTitle: '',
  }
  const type = rawType || categoryCode

  const updateFiltersParams = () => {
    const url = `&${new URLSearchParams(filters).toString()}`
    return url
  }

  const {
    data: respData,
    status,
    refetch,
    isFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery<TCategoryStocks, unknown, TCategoryStocks>(
    [`indianstocksListing${categoryCode}`],
    async ({ pageParam = 0 }) => {
      const response = fetchRequest({
        config: {
          headers,
        },
        timeout: 0,
        url: `${
          custHeaderPresent
            ? IN_STOCKS_BG_LISTING_API_URL
            : IN_STOCKS_LISTING_API_URL
        }?category=${type}&offset=${
          pageParam * 12
        }&limit=12&active=true${updateFiltersParams()}`,
      })
      return response
    },
    {
      initialData: listingData
        ? {
            pageParams: [0],
            pages: [listingData],
          }
        : undefined,
      enabled:
        !listingData?.data ||
        isEmpty(listingData) ||
        listingApiFailedStatusCode !== 200,
      getNextPageParam: (_, allPages) => {
        if (allPages.length < allPages[0]?.pageCount) {
          return allPages.length
        } else {
          return undefined
        }
      },
    },
  )
  const { data: filtersData, refetch: refetchFilters } =
    useQuery<TCategoryFiltersStocks>(
      [`indianstocksListingFilters${categoryCode}`],
      () =>
        fetchRequest({
          config: {
            headers,
          },
          timeout: 0,
          url: `${
            custHeaderPresent
              ? IN_STOCKS_BG_LISTING_FILTERS
              : IN_STOCKS_LISTING_FILTERS
          }?category=${type}`,
        }),
      {
        retry: 0,
        staleTime: 300,
      },
    )
  const pages = respData?.pages ?? {}
  const {
    pageCount = 1,
    categoryList = [],
    desc,
    heading,
    custom_column_key,
  } = pages[0] ?? {}
  const resultsList = respData?.pages
    .filter(Boolean)
    .flat()
    .map(el => el.data)
    .flat()
    .filter(Boolean)

  const { data: filtersList } = filtersData ?? {}
  const {
    stockTypeList = [],
    sortKeys,
    filters_disabled = false,
    filterList: filtersMap = [],
  } = filtersList ?? {}
  stockDataTypeList.current = stockTypeList
  const sortingKeys = sortKeys ?? SortKeys
  const sortedKeys = sortingKeys.filter(({ key }) => key === filters?.sortKey)

  const onFilterUpdate = ({
    type = '',
    key,
    value,
    selectionKey,
  }: {
    type: string
    key: string | number
    value: string
    selectionKey: string
  }) => {
    switch (type) {
      case 'multi':
        {
          const isItemExist = filters?.[selectionKey]?.includes(value)
          const sectionList = isItemExist
            ? filters?.[selectionKey]?.filter(item => item !== value)
            : filters?.[selectionKey]?.length
              ? [value].concat(filters?.[selectionKey])
              : [value]
          dispatch({
            type: 'UPDATE_FILTERS',
            filters: {
              ...filters,
              [selectionKey]: sectionList,
            },
          })
        }
        break
      case 'single':
        dispatch({
          type: 'UPDATE_FILTERS',
          filters: {
            ...filters,
            [selectionKey]: filters[selectionKey] === value ? '' : value,
          },
        })
        break
      case 'sortOrder':
        dispatch({
          type: 'UPDATE_FILTERS',
          filters: {
            ...filters,
            [type]: key,
          },
        })
        break
      case 'sortKey':
        dispatch({
          type: 'UPDATE_FILTERS',
          filters: {
            ...filters,
            [type]: key,
            sortOrder: SortKeys.filter(({ key: k }) => k === key)[0]?.subList[0]
              ?.key,
          },
        })
        break
      default:
        break
    }
  }
  const listResults = resultsList
  const { asPath } = router
  const categoryData =
    categoryList.filter(({ relativepath }) => relativepath === asPath)?.[0] ??
    {}

  const pushEvent = useAnalyticsEvent()

  useEffect(() => {
    const eventAction =
      window.innerWidth < 568 ? 'landed_l1_mweb' : 'landed_l1_dweb'
    pushEvent({ gaPayload: { eventAction, eventCategory: 'landed_on_page' } })
  }, [pushEvent])

  const { name = '', description = '' } = selectedSlug[0]
    ? selectedSlug[0]
    : {
        name: categoryData?.title,
        description: categoryData?.description,
      }
  const selectedChip = typeList.filter(item => item.selected)
  const chipName = selectedChip[0]?.name ? selectedChip[0]?.name : name
  const chipNavLink = selectedChip[0]?.relativepath ?? ''

  useEffect(() => {
    if (updateCalCount) {
      void refetch()
      void refetchFilters()
    }
  }, [refetch, refetchFilters, updateCalCount])

  let EmptyState: null | JSX.Element = null
  if (listResults?.length === 0 && status === 'success' && !isFetching) {
    EmptyState = <EmptyWidget />
  }

  if (
    listResults?.length === 0 &&
    status === 'success' &&
    Object.keys(filters).length
  ) {
    EmptyState = <NoFilters />
  }

  const flatData = respData?.pages.filter(Boolean).flat()

  const columnList1 = flatData?.[0].columnList ?? []

  const showOldLayout = !columnList1.length

  const getFilterWidget = () => {
    if (filters_disabled || !filtersMap.length) return null
    return (
      <div
        className="flex cursor-pointer items-center justify-end rounded-md border p-2"
        onClick={() =>
          dispatch({ type: 'TOGGLE_FILTER_MODAL', showFilterModal: true })
        }
      >
        <Filter width={18} height={18} />
        <Text
          appearance="caption"
          addClassName="ml-2 text-brand-blue-azureRadiance"
        >
          Filter
        </Text>
      </div>
    )
  }

  return (
    <div className="bg-brand-white">
      {!fromStrapi && (
        <SEOHeader
          noIndex={categoryData?.noindex ?? undefined}
          data={{
            title: seoTitle
              ? seoTitle
              : categoryData?.seoTitle || categoryData?.title,
            description: seoDescription
              ? seoDescription
              : categoryData?.seoDescription || categoryData?.description,
            canonicalUrl: DOMAIN + router.asPath.split('?')[0],
          }}
        />
      )}
      <div className={classNames({ 'herolike-wrapper': showSidebar })}>
        {!fromStrapi && (
          <div className="flex">
            <BackNavigation link="/stocks" childrenStyle="z-40">
              <BreadCrumb
                extraClassNames="pl-0"
                breadCrumbList={[
                  { label: 'Home', link: '/' },
                  {
                    label: 'Stocks',
                    link: '/stocks',
                  },
                  { label: chipName, link: chipNavLink },
                ].concat(
                  categoryData?.title
                    ? [{ label: categoryData?.title, link: router.asPath }]
                    : [],
                )}
              />
            </BackNavigation>
            <div className="hidden lg:flex">
              <StencilCDNImage
                src="https://indcdn.indmoney.com/cdn/images/fe/indstocks-logo.png"
                alt="INDstocks by INDmoney"
                width={140}
                height={50}
                className="xl-hidden shrink-0"
              />
            </div>
          </div>
        )}

        <div
          className={classNames(
            'flex flex-col-reverse gap-8 pt-2 md:flex-row',
            {
              'pt-4': !fromStrapi,
            },
          )}
        >
          {showSidebar && (
            <div
              className={classNames(
                'no-scrollbar sticky top-20 w-full self-center overflow-y-auto bg-white md:top-24 md:h-[100vh] md:w-3/10 md:self-start',
                {
                  'my-3': type === 'all',
                },
              )}
            >
              {type === 'all' && !isMobile ? (
                <StockFilters
                  onReset={() => {
                    dispatch({
                      type: 'UPDATE_FILTERS',
                      filters: {},
                    })
                  }}
                  filters={filters}
                  className="no-scrollbar max-h-[70vh] overflow-y-auto"
                  onFilterUpdate={onFilterUpdate}
                  key=""
                  accordianMap={filtersMap}
                  forMobile={isMobile}
                />
              ) : (
                <SidebarNav linksData={stockCategories.data.search_nav[0]} />
              )}
            </div>
          )}
          <div className="flex w-full flex-1 flex-col md:w-7/10">
            {!fromStrapi && (
              <section id="l1-header">
                <div className="flex items-center justify-between">
                  <Text appearance="heading2" tag="h1">
                    {heading ?? name}
                  </Text>
                </div>
                <div className="mt-2 text-sm text-brand-grey-base md:text-base md:font-normal">
                  {(desc ?? description)
                    ? description
                    : categoryData?.description}
                </div>
              </section>
            )}
            {(isMobile || type !== 'all') && (
              <div className="flex justify-end gap-x-2.5">
                {getFilterWidget()}
              </div>
            )}

            {/* New Dynamic Column Layout   */}
            {!showOldLayout && (
              <StockListingDynamicTable
                columnList1={columnList1}
                resultsList={resultsList}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                eventsData={{
                  listClickEvent:
                    eventMapping[eventData.stocksCategoryListClick],
                }}
              />
            )}

            {showOldLayout &&
              (EmptyState ? (
                <div>{EmptyState}</div>
              ) : (
                <StocksListingTraitTable
                  data={listResults}
                  activeSortKey={filters?.sortKey}
                  activeSortOrder={filters?.sortOrder}
                  onFilterUpdate={onFilterUpdate}
                  sortKeys={SortKeys}
                  custom_column_key={custom_column_key}
                  headingApplicableMTF="MTF Boost"
                  includeBoostColumn={includeBoostColumn}
                />
              ))}
            {pageOffset < pageCount - 1 && showOldLayout && (
              <div className="flex justify-center">
                <button
                  className="btn btn-medium mt-10 md:w-1/3"
                  onClick={() => {
                    dispatch({
                      type: 'PAGE_COUNT',
                      pageOffset: pageOffset + 1,
                    })
                    void fetchNextPage()
                  }}
                >
                  View More
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {showFilterModal && (
        <Modal
          bodyClassName="max-w-lg"
          showModal={showFilterModal}
          onClose={() =>
            dispatch({
              type: 'TOGGLE_FILTER_MODAL',
              showFilterModal: false,
            })
          }
        >
          <StockFilters
            onReset={() => {
              dispatch({
                type: 'UPDATE_FILTERS',
                filters: {},
              })
            }}
            filters={filters}
            className="no-scrollbar max-h-[70vh] overflow-y-auto"
            onFilterUpdate={onFilterUpdate}
            key=""
            accordianMap={filtersMap}
            forMobile={isMobile}
          />
        </Modal>
      )}
      <Modal
        showModal={showModal}
        onClose={() =>
          dispatch({
            type: 'TOGGLE_MODAL',
            showModal: false,
          })
        }
      >
        <Sorting
          sortList={[
            {
              name: sortedKeys[0]?.value,
              key: 'sortKey',
              orderList: sortKeys ? sortKeys : SortKeys,
            },
            {
              name:
                sortedKeys[0]?.subList?.filter(
                  ({ key }) => key === filters?.sortOrder,
                )[0]?.value ?? '',
              key: 'sortOrder',
              orderList: sortedKeys[0]?.subList,
            },
          ]}
          onSortUpdate={({ payload, orderList, type }) => {
            const key = orderList?.filter(({ value }) => value === payload)?.[0]
              .key
            onFilterUpdate({ key, type, value: '', selectionKey: '' })
          }}
        />
      </Modal>
    </div>
  )
}

export default INListing
